// Shared ArcGIS Feature Service rendering: the esri-leaflet crash guard plus building
// Arcade-shim-aware feature layers. Used by the track map (map-base.js) and the MapLayer admin
// preview (map.mjs). Leaflet is reached via window.L (external/global in the tracking bundle,
// bundled in the map bundle) — the single instance esri-leaflet attaches `.esri` to.

import { prepareArcadeRenderer } from "./arcade-renderer.mjs";
import { resolveArcgisSublayerUrls, parseSublayerIds, arcgisUrlShape } from "./arcgis-service.mjs";
import { fetchItemLayerOverrides } from "./arcgis-item-overrides.mjs";
import { buildFieldPresentation } from "./arcgis-popup-fields.mjs";

// esri-leaflet v3's feature grid throws during map load (_update/_isValidCell on an undefined
// _cells): Leaflet wires the layer's move handlers before onAdd initializes the grid, so an
// in-flight move can drive _update before _cells exists. Guard at the _update chokepoint. One-time
// idempotent prototype patch; walks the chain so it survives minification and bundle changes.
export function patchEsriFeatureGrid() {
	const L = window.L;
	if (L == null || L.esri == null || L.esri.FeatureManager == null) {
		return;
	}
	let proto = L.esri.FeatureManager.prototype;
	while (proto && !Object.prototype.hasOwnProperty.call(proto, "_update")) {
		proto = Object.getPrototypeOf(proto);
	}
	if (proto == null || proto._esriUpdateGuarded) {
		return;
	}
	const originalUpdate = proto._update;
	proto._update = function (center) {
		// _cells is the first thing the grid's onAdd assigns; until it exists this is a premature
		// call, so skip it — the real onAdd -> _resetView -> _update runs a beat later.
		if (this._cells == null) {
			return;
		}
		return originalUpdate.call(this, center);
	};
	proto._esriUpdateGuarded = true;
}

// esri-leaflet's RasterLayer (dynamicMapLayer's base) races its own removal: an export request
// still in flight when the layer is removed calls _renderImage on arrival, which adds the image
// overlay directly to the map — orphaned, with no owner left to remove it. Observed on the admin
// preview when a rebuild (new URL) tears the layer down while its export loads; the same race
// strands imagery on the track map when an image-mode layer is toggled off mid-export. Guard at
// the _renderImage chokepoint: Leaflet nulls _map on removal, so late arrivals no-op. One-time
// idempotent prototype patch, same pattern as patchEsriFeatureGrid above.
export function patchEsriRasterOrphan() {
	const L = window.L;
	if (L == null || L.esri == null || L.esri.RasterLayer == null) {
		return;
	}
	let proto = L.esri.RasterLayer.prototype;
	while (proto && !Object.prototype.hasOwnProperty.call(proto, "_renderImage")) {
		proto = Object.getPrototypeOf(proto);
	}
	if (proto == null || proto._esriRenderImageGuarded) {
		return;
	}
	const originalRenderImage = proto._renderImage;
	proto._renderImage = function () {
		if (this._map == null) {
			return;
		}
		return originalRenderImage.apply(this, arguments);
	};
	proto._esriRenderImageGuarded = true;
}

// esri-leaflet-renderers defers the real onAdd until the layer's metadata request resolves (its
// decorated instance onAdd runs the original from the metadata callback). A layer removed while
// that request is in flight has _map nulled by Leaflet's removal, and the late callback then
// resurrects it: onAdd -> GridLayer._resetView -> this._map.getCenter() throws. Observed on the
// MapLayer admin preview, whose initial load rebuilds the overlay several times in quick
// succession; the same race hits the track map when a layer is toggled off right after on.
// Leaflet always sets _map BEFORE a legitimate onAdd runs, so a null _map here is only ever the
// dead-resurrection case. The patch must install before layer construction: the decorator binds
// the prototype onAdd at initialize, capturing whatever is on the chain at that moment. Same
// one-time idempotent chain-walking pattern as the guards above.
export function patchEsriDeferredOnAdd() {
	const L = window.L;
	if (L == null || L.esri == null || L.esri.FeatureLayer == null) {
		return;
	}
	let proto = L.esri.FeatureLayer.prototype;
	while (proto && !Object.prototype.hasOwnProperty.call(proto, "onAdd")) {
		proto = Object.getPrototypeOf(proto);
	}
	if (proto == null || proto._esriOnAddGuarded) {
		return;
	}
	const originalOnAdd = proto.onAdd;
	proto.onAdd = function (map) {
		if (this._map == null) {
			return;
		}
		return originalOnAdd.call(this, map);
	};
	proto._esriOnAddGuarded = true;
}

// Decides how a sublayer should be rendered from its service drawingInfo. Exported so the decision is
// unit-tested — it has regressed before (over-broadly ignoring the renderer drew every native renderer
// in esri-leaflet's default blue instead of the service symbology). Returns { mode, descriptor }:
//   "native"  -> esri-leaflet-renderers draws the service renderer itself (simple/uniqueValue/
//                classBreaks, no Arcade); the decorator MUST run — do NOT ignoreRenderer.
//   "shim"    -> a supported Arcade valueExpression; descriptor carries the rewritten drawingInfo,
//                synthetic field, and evaluator.
//   "default" -> no drawingInfo, or a renderer the decorator can't classify (unsupported Arcade
//                valueExpression / renderer type); the caller draws esri-leaflet's default
//                (ignoreRenderer:true).
export function planSublayerRender(drawingInfo) {
	const descriptor = prepareArcadeRenderer(drawingInfo);
	if (descriptor == null) {
		return { mode: drawingInfo == null ? "default" : "native", descriptor: null };
	}
	return { mode: descriptor.supported ? "shim" : "default", descriptor: descriptor };
}

// Render plan for a sublayer given the service drawingInfo and an ArcGIS Online item-authored
// override (null when none). The override wins when present — it is what Map Viewer shows for
// the layer — and in native mode it must be handed to esri-leaflet as options.drawingInfo (the
// renderers decorator swaps it in over the metadata it fetches itself, the same hook the Arcade
// shim uses). Without an override no option is set and the decorator reads the service metadata,
// exactly as before. Exported for unit tests.
export function planEffectiveRender(serviceDrawingInfo, overrideDrawingInfo) {
	// The renderers decorator swaps options.drawingInfo in only when the service metadata itself
	// carries drawingInfo; without it the decorated onAdd never delegates and the layer would
	// draw nothing at all. Ignore the override there — the plain visible default wins.
	if (serviceDrawingInfo == null) {
		const plan = planSublayerRender(null);
		return { mode: plan.mode, descriptor: plan.descriptor, drawingInfoOption: null };
	}
	const effective = overrideDrawingInfo != null ? overrideDrawingInfo : serviceDrawingInfo;
	const plan = planSublayerRender(effective);
	return {
		mode: plan.mode,
		descriptor: plan.descriptor,
		drawingInfoOption: plan.mode === "native" && overrideDrawingInfo != null ? overrideDrawingInfo : null
	};
}

// Popup field presentation for a sublayer, from its service metadata + the item's authored
// popupInfo (null when there's nothing to present — the popup then falls back to the raw
// property dump). Exported for unit tests.
export function buildSublayerPresentation(metadata, popupInfo) {
	if (metadata == null || typeof metadata !== "object") {
		return null;
	}
	return buildFieldPresentation(metadata.fields, popupInfo, metadata.displayField);
}

// Feature-mode payload relief: ask the service for zoom-appropriate generalization and capped
// coordinate precision. Fixed constants, not per-layer UI (see the design spec).
const FEATURE_SIMPLIFY_FACTOR = 0.5;
const FEATURE_PRECISION = 6;

// Builds one esri-leaflet featureLayer for a specific .../FeatureServer/{n} URL, applying the
// Arcade-renderer shim when the service's renderer uses a supported valueExpression (so the
// service's own symbology renders even though esri-leaflet-renderers has no Arcade engine). Always
// resolves to a layer: an unsupported expression or a metadata failure falls back to a plain layer.
// opts: { minZoom, maxZoom, where, log, bindPopup } — bindPopup(featureLayer, presentation) binds
// the popup (presentation is the popup field plan from buildSublayerPresentation, null when the
// metadata offered nothing to present);
// log(msg) warns.
export function buildShimmedFeatureLayer(sublayerUrl, opts) {
	const L = window.L;
	const baseUrl = sublayerUrl.split("?")[0];
	const log = opts.log || (() => {});

	// Only include min/max zoom when set to a number. esri's visibility guard is
	// `zoom > maxZoom || zoom < minZoom`; a null maxZoom coerces to 0, hiding the layer at every
	// real zoom, whereas omitting it (undefined) renders at all zooms.
	function layerOptions(extra) {
		const layerOpts = { url: sublayerUrl, simplifyFactor: FEATURE_SIMPLIFY_FACTOR, precision: FEATURE_PRECISION };
		if (opts.where != null && opts.where !== "") {
			layerOpts.where = opts.where;
		}
		if (opts.minZoom != null) {
			layerOpts.minZoom = opts.minZoom;
		}
		if (opts.maxZoom != null) {
			layerOpts.maxZoom = opts.maxZoom;
		}
		if (extra != null) {
			Object.assign(layerOpts, extra);
		}
		return layerOpts;
	}

	function finish(featureLayer, presentation) {
		if (opts.bindPopup) {
			opts.bindPopup(featureLayer, presentation == null ? null : presentation);
		}
		return featureLayer;
	}

	// ignoreRenderer:true bypasses the esri-leaflet-renderers decorator and draws esri-leaflet's default
	// symbology. Use it ONLY when the decorator can't render the service symbology anyway (no
	// drawingInfo, or an Arcade valueExpression / renderer type it can't handle). For a native
	// renderer the decorator MUST run so the service's own symbology — e.g. a simple red polygon — is
	// applied; forcing ignoreRenderer there draws every feature in the default blue.
	function buildPlainLayer(ignoreRenderer, presentation) {
		return finish(L.esri.featureLayer(layerOptions(ignoreRenderer ? { ignoreRenderer: true } : null)), presentation);
	}

	return fetch(`${baseUrl}?f=json`, { headers: { Accept: "application/json" } })
		.then((response) => {
			// fetch only rejects on network/CORS failure, not HTTP errors. Throw on an HTTP error so the
			// .catch below logs it and falls back to a plain layer, instead of parsing an error body as
			// service metadata.
			if (!response.ok) {
				throw new Error(`HTTP ${response.status}`);
			}
			return response.json();
		})
		.then((metadata) => {
			// The item-override lookup never rejects: a service without a public *.arcgis.com item
			// resolves null and the service drawingInfo drives rendering exactly as before.
			const sublayerId = arcgisUrlShape(baseUrl).sublayerId;
			const overridesPromise = metadata != null && sublayerId !== null
				? fetchItemLayerOverrides(metadata.serviceItemId, baseUrl, sublayerId)
				: Promise.resolve({ drawingInfo: null, popupInfo: null });
			return overridesPromise.then((overrides) => {
				const presentation = buildSublayerPresentation(metadata, overrides.popupInfo);
				const plan = planEffectiveRender(metadata == null ? null : metadata.drawingInfo, overrides.drawingInfo);
				if (plan.mode === "native") {
					if (plan.drawingInfoOption != null) {
						// Item-authored symbology: the decorator must use it instead of the service
						// drawingInfo it fetches itself.
						return finish(L.esri.featureLayer(layerOptions({ drawingInfo: plan.drawingInfoOption })), presentation);
					}
					// esri-leaflet-renderers renders the service's own renderer; the decorator must run, so
					// do NOT ignore it (this is the common case — a simple red polygon renders red).
					return buildPlainLayer(false, presentation);
				}
				if (plan.mode === "default") {
					if (plan.descriptor != null) {
						log(`ArcGIS sublayer ${sublayerUrl}: renderer uses an Arcade valueExpression outside the supported subset; rendering without service symbology.`);
					}
					// No usable service symbology — draw esri-leaflet's visible default.
					return buildPlainLayer(true, presentation);
				}
				const descriptor = plan.descriptor;
				const featureLayer = L.esri.featureLayer(layerOptions({ drawingInfo: descriptor.drawingInfo }));
				// Stamp the synthetic classifying field onto each feature before esri-leaflet builds its
				// symbol (createLayers runs before pointToLayer/style; re-stamping is idempotent).
				const createLayers = featureLayer.createLayers;
				featureLayer.createLayers = function (features) {
					for (const feature of features) {
						if (feature != null && feature.properties != null) {
							feature.properties[descriptor.field] = descriptor.evaluate(feature.properties);
						}
					}
					return createLayers.call(this, features);
				};
				return finish(featureLayer, presentation);
			});
		})
		.catch((error) => {
			log(`ArcGIS sublayer ${sublayerUrl}: failed to load metadata (${error}); rendering without service symbology.`);
			// No metadata to drive the decorator — draw a visible default.
			return buildPlainLayer(true);
		});
}

// Resolves a stored URL + selected-id CSV to concrete sublayer URLs and builds each shimmed layer.
// Resolves to a featureLayer[] (empty when the URL resolves to no sublayers).
export function buildArcgisSublayers(url, layersCsv, opts) {
	const urls = resolveArcgisSublayerUrls(url, layersCsv);
	if (urls.length === 0) {
		return Promise.resolve([]);
	}
	return Promise.all(urls.map((sublayerUrl) => buildShimmedFeatureLayer(sublayerUrl, opts)));
}

// Options for L.esri.dynamicMapLayer, which renders a service root's selected sublayers as one
// server-drawn image per viewport (the ArcGIS REST export?bbox operation). Pure and exported for
// unit tests. The stored CSV is the same one feature mode uses; a filter becomes a layerDefs
// entry per selected id. Always transparent: the layer is an overlay above the base map.
// Overlay opacity for a stored ArcGIS layer. Only the server-rendered image mode has a single
// opacity knob (the export image); feature mode draws the service's authored symbology, whose
// alphas are the author's choice, so any stored value is ignored there. Accepts the 0-1 fraction
// as a number or an invariant-culture numeric string; garbage resolves null (opaque default) and
// out-of-range values clamp. Exported for unit tests.
export function resolveOverlayOpacity(renderMode, rawOpacity) {
	if (renderMode !== "image" || rawOpacity == null || rawOpacity === "") {
		return null;
	}
	const parsed = typeof rawOpacity === "number" ? rawOpacity : parseFloat(rawOpacity);
	if (!Number.isFinite(parsed)) {
		return null;
	}
	return Math.min(Math.max(parsed, 0), 1);
}

export function buildDynamicMapLayerOptions(url, layersCsv, filter, opts) {
	const dynamicOptions = {
		url: url.split("?")[0].split("#")[0].replace(/\/+$/, ""),
		layers: parseSublayerIds(layersCsv),
		format: "png32",
		transparent: true,
	};
	if (filter != null && filter !== "") {
		const layerDefs = {};
		for (const id of dynamicOptions.layers) {
			layerDefs[id] = filter;
		}
		dynamicOptions.layerDefs = layerDefs;
	}
	if (opts.minZoom != null) {
		dynamicOptions.minZoom = opts.minZoom;
	}
	if (opts.maxZoom != null) {
		dynamicOptions.maxZoom = opts.maxZoom;
	}
	if (opts.opacity != null) {
		dynamicOptions.opacity = opts.opacity;
	}
	return dynamicOptions;
}

// Builds the overlay for a stored ArcGIS layer in either render mode.
//   params: { renderMode: "features"|"image", filter: string|null } (parseMapLayerParameters)
//   opts:   { minZoom, maxZoom, opacity, log, bindPopup }
// Feature mode resolves to the existing shimmed featureLayer(s) (an L.layerGroup when several);
// image mode to a single L.esri.dynamicMapLayer — its popups come from the merged identify
// pipeline (arcgis-identify.mjs registerIdentifyLayer), not a per-layer binding here. Resolves
// to null when nothing can render (no usable URL/ids).
export function buildArcgisOverlay(url, layersCsv, params, opts) {
	const L = window.L;
	if (params.renderMode === "image") {
		if (typeof url !== "string" || url === "" || parseSublayerIds(layersCsv).length === 0) {
			return Promise.resolve(null);
		}
		return Promise.resolve(L.esri.dynamicMapLayer(buildDynamicMapLayerOptions(url, layersCsv, params.filter, opts)));
	}
	return buildArcgisSublayers(url, layersCsv, {
		minZoom: opts.minZoom,
		maxZoom: opts.maxZoom,
		where: params.filter,
		log: opts.log,
		bindPopup: opts.bindPopup,
	}).then((featureLayers) => {
		if (featureLayers.length === 0) {
			return null;
		}
		return featureLayers.length === 1 ? featureLayers[0] : L.layerGroup(featureLayers);
	});
}
